// React
import { useEffect, useState } from "react";

// Third-Party
import {
  Backdrop,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowLeftRight, Check, ChevronDown, LogOut, Plus } from "lucide-react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

// Internal
import {
  endAddingAccount,
  getAvailableAccounts,
  getRunningAccount,
  hasUnsavedChanges,
  signOutAccount,
  startAddingAccount,
  switchAccount,
  takeLogoutNotice,
  takeSignedInNotice,
} from "src/common/accountSession";
import Avatar from "src/components/Avatar";
import Label from "src/components/Label";
import useLocales from "src/hooks/useLocales";
import { useSelector } from "src/redux/store";
import { PATH_AUTH } from "src/routes/paths";
import { AccountMenuItem } from "./AccountLogoutMenu";
import createAvatar from "src/utils/createAvatar";
import { textStyle } from "@utils/cssStyles";

// Types
import { IAccountSession } from "src/@types/auth";

// ----------------------------------------------------------------------

export function useAccountNotice() {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  useEffect(() => {
    endAddingAccount();
    const email = takeLogoutNotice();
    if (email) {
      enqueueSnackbar(translate("multi_account.logged_out", { email }), { variant: "warning" });
    }
    const signedInEmail = takeSignedInNotice();
    if (signedInEmail) {
      enqueueSnackbar(translate("multi_account.already_signed_in", { email: signedInEmail }), {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, translate]);
}

// ----------------------------------------------------------------------

type TAccountRowProps = {
  account: IAccountSession;
  isActive?: boolean;
  onSwitch?: () => void;
  onRemove?: () => void;
};

const AccountRow = ({ account, isActive, onSwitch, onRemove }: TAccountRowProps) => {
  const theme = useTheme();
  const { translate } = useLocales();
  const liveFirstName = useSelector((state: any) => state.credential.userCred?.firstName);
  const firstName = (isActive && liveFirstName) || account.profile?.firstName || "";
  const avatar = createAvatar(firstName);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      onClick={onSwitch}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        ...(isActive
          ? { bgcolor: alpha(theme.palette.primary.main, 0.08) }
          : {
              cursor: "pointer",
              "&:hover": { bgcolor: theme.palette.action.hover },
              "& .account-remove": { opacity: 0 },
              "&:hover .account-remove": { opacity: 1 },
            }),
      }}
    >
      <Avatar color={avatar.color} sx={{ width: 32, height: 32, fontSize: 14 }}>
        {avatar.name}
      </Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {firstName}
        </Typography>
        <Typography component="p" variant="caption" color="text.label" noWrap>
          {account.email}
        </Typography>
      </Box>
      {isActive ? (
        <Check size={18} color={theme.palette.primary.main} />
      ) : (
        <Tooltip title={translate("multi_account.remove_account")}>
          <IconButton
            className="account-remove"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onRemove?.();
            }}
          >
            <LogOut size={16} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

// ----------------------------------------------------------------------

type TProps = {
  onClose: () => void;
  isListOpen: boolean;
  onToggleList: () => void;
};

export default function AccountSwitcher({
  onClose,
  isListOpen,
  onToggleList,
}: TProps) {
  const theme = useTheme();
  const { translate } = useLocales();
  const navigate = useNavigate();
  const [isSwitching, setIsSwitching] = useState(false);
  const [, refresh] = useState(0);

  const current = getRunningAccount();
  const others = getAvailableAccounts().filter(
    ({ accountId }) => accountId !== current?.accountId,
  );

  const handleSwitch = (accountId: string) => {
    if (!hasUnsavedChanges()) setIsSwitching(true);
    switchAccount(accountId);
  };

  const handleRemove = async (accountId: string) => {
    await signOutAccount(accountId);
    refresh((n) => n + 1);
  };

  const handleAdd = () => {
    onClose();
    startAddingAccount();
    navigate(PATH_AUTH.login);
  };

  return (
    <Box sx={{ p: 1, borderBottom: `0.5px solid ${theme.palette.divider}` }}>
      {current && (
        <>
          <Typography
            variant="caption"
            sx={[{ px: 1, mb: 0.5, display: "block" }, textStyle("label", 700, 12)]}
          >
            {translate("multi_account.current_account")}
          </Typography>
          <AccountRow account={current} isActive />
        </>
      )}

      {others.length > 0 && (
        <>
          <AccountMenuItem
            icon={ArrowLeftRight}
            title={translate("multi_account.switch_account")}
            onClick={onToggleList}
            endIcon={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Label variant="ghost" color="default">
                  {others.length}
                </Label>
                <ChevronDown
                  size={16}
                  color={theme.palette.text.label}
                  style={{
                    transition: "transform 0.2s",
                    transform: isListOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </Stack>
            }
          />
          <Collapse in={isListOpen} unmountOnExit>
            <Box sx={{ pl: 1, maxHeight: 176, overflowY: "auto" }}>
              {others.map((account) => (
                <AccountRow
                  key={account.accountId}
                  account={account}
                  onSwitch={() => handleSwitch(account.accountId)}
                  onRemove={() => handleRemove(account.accountId)}
                />
              ))}
            </Box>
          </Collapse>
        </>
      )}

      <AccountMenuItem
        icon={Plus}
        title={translate("multi_account.add_account")}
        onClick={handleAdd}
      />

      <Backdrop open={isSwitching} sx={{ zIndex: theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
